import React, {useState, useMemo} from 'react';
import {
    Detail,
    Info,
    MaterialAndInfoBox,
    MaterialContainer,
    NoShrinkInfoBox,
} from 'components/customer/JobDashboard/columns/ProductDetail';
import {Preview} from 'components/customer/JobDashboard/columns/Preview';
import {Information} from 'components/customer/JobDashboard/columns/Information';
import {Product} from 'components/customer/Product/entity/Product';
import {useAppContext} from 'contexts';
import {isDeviceSmall} from 'shared/helpers/DeviceSize';
import {parseHtmlString} from 'shared/helpers/HTMLParser';
import {mapDoor} from 'components/customer/Materials/helper/doorHelper';
import {mapMaterialEdge} from 'components/customer/Materials/helper/edgeHelper';
import {transformDisplayName} from 'components/customer/Materials/helper';

interface SelectionsOverviewProps {
    product: Product;
    isProductVisualiser?: boolean;
    compact?: boolean;
    infoDisplayOverride?: boolean;
}

export enum SelectionsType {
    STYLE = 'Style',
    EXTERIOR = 'Exterior',
    CARCASE = 'Carcase',
}

export const SelectionsOverview = ({
    product,
    isProductVisualiser = false,
    compact,
    infoDisplayOverride,
}: SelectionsOverviewProps) => {
    const {userProfile} = useAppContext();
    const [activeSelectionType, setActiveSelectionType] =
        useState<SelectionsType | null>(null);
    const detail = useMemo(() => {
        switch (activeSelectionType) {
            case SelectionsType.STYLE:
                return product.door ? product.door.name : '';

            case SelectionsType.EXTERIOR: {
                if (!product.extMaterial) return '';
                const transformed = transformDisplayName(product.extMaterial);
                return transformed.displayName;
            }

            case SelectionsType.CARCASE: {
                if (!product.carcMaterial) return '';
                const transformed = transformDisplayName(product.carcMaterial);
                return transformed.displayName;
            }

            default:
                return '';
        }
    }, [activeSelectionType, product]);

    const clickHandler = useMemo(() => {
        return !isProductVisualiser ? setActiveSelectionType : undefined;
    }, [isProductVisualiser, setActiveSelectionType]);

    const isSmallDevice = isDeviceSmall();
    // this field will work to detect doors/drawers but if we can have a flag from room products API that would be better
    const hasExteriorDoor = product?.job_cabinet_door_advanced !== null;

    return (
        <MaterialAndInfoBox
            className="selection-container"
            $isMobile={isSmallDevice || compact}>
            <Info $isMobile={isSmallDevice}>
                <NoShrinkInfoBox
                    className="selection-card"
                    $isMobile={isSmallDevice || compact}>
                    <h1>Selections</h1>
                    <MaterialContainer $isMobile={isSmallDevice}>
                        {hasExteriorDoor ? (
                            <Preview
                                door={mapDoor(product.door)}
                                type={SelectionsType.STYLE}
                                setActiveSelectionType={clickHandler}
                            />
                        ) : null}
                        <Preview
                            material={product.extMaterial}
                            edge={mapMaterialEdge(product.ext_edge)}
                            type={SelectionsType.EXTERIOR}
                            isHorizontalGrain={product.ext_hor_grain == 1}
                            setActiveSelectionType={clickHandler}
                        />
                        <Preview
                            material={product.carcMaterial}
                            edge={mapMaterialEdge(product.carc_edge)}
                            type={SelectionsType.CARCASE}
                            isHorizontalGrain={product.carc_hor_grain == 1}
                            setActiveSelectionType={clickHandler}
                        />
                    </MaterialContainer>
                </NoShrinkInfoBox>
                {(userProfile?.allowSupplyOptionIcons &&
                    (!isSmallDevice || infoDisplayOverride)) ||
                isProductVisualiser ? (
                    <Information product={product} />
                ) : null}
            </Info>
            <Detail>{parseHtmlString(detail)}</Detail>
        </MaterialAndInfoBox>
    );
};
